import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  email: z.string().email('Please enter a valid email address'),
  service: z.string().min(1, 'Please select a service'),
  appointment_datetime: z.string().optional(),
  details: z.string().optional(),
});

export const LeadCaptureModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      service: '',
      appointment_datetime: '',
      details: '',
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, data);
      
      toast.success(t('form.success'));
      
      // Redirect to SMS after 2 seconds
      setTimeout(() => {
        const message = encodeURIComponent(
          `Hi EDX Construction! I just submitted a request for a free estimate. Looking forward to hearing from you!`
        );
        // Use first phone number for SMS redirect
        window.location.href = `sms:+14074510902?body=${message}`;
      }, 2000);
      
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error(t('form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto"
        data-testid="lead-capture-modal"
      >
        <DialogHeader>
          <DialogTitle 
            className="text-2xl font-bold text-[hsl(215,75%,16%)]"
            style={{ fontFamily: 'Outfit' }}
          >
            {t('form.title')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="lead-capture-form">
            {/* Name Field */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('form.name')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('form.namePlaceholder')}
                      {...field}
                      className="border-gray-300 focus:border-[hsl(224,64%,33%)] focus:ring-2 focus:ring-[hsl(224,64%,33%)]/20"
                      data-testid="lead-form-name-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone Field */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('form.phone')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder={t('form.phonePlaceholder')}
                      {...field}
                      className="border-gray-300 focus:border-[hsl(224,64%,33%)] focus:ring-2 focus:ring-[hsl(224,64%,33%)]/20"
                      data-testid="lead-form-phone-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('form.email')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('form.emailPlaceholder')}
                      {...field}
                      className="border-gray-300 focus:border-[hsl(224,64%,33%)] focus:ring-2 focus:ring-[hsl(224,64%,33%)]/20"
                      data-testid="lead-form-email-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Service Field */}
            <FormField
              control={form.control}
              name="service"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('form.service')}
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger 
                        className="border-gray-300 focus:border-[hsl(224,64%,33%)] focus:ring-2 focus:ring-[hsl(224,64%,33%)]/20"
                        data-testid="lead-form-service-select"
                      >
                        <SelectValue placeholder={t('form.servicePlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pavers" data-testid="service-option-pavers">
                        {t('form.serviceOptions.pavers')}
                      </SelectItem>
                      <SelectItem value="kitchen" data-testid="service-option-kitchen">
                        {t('form.serviceOptions.kitchen')}
                      </SelectItem>
                      <SelectItem value="bathroom" data-testid="service-option-bathroom">
                        {t('form.serviceOptions.bathroom')}
                      </SelectItem>
                      <SelectItem value="interior" data-testid="service-option-interior">
                        {t('form.serviceOptions.interior')}
                      </SelectItem>
                      <SelectItem value="drywall" data-testid="service-option-drywall">
                        {t('form.serviceOptions.drywall')}
                      </SelectItem>
                      <SelectItem value="general" data-testid="service-option-general">
                        {t('form.serviceOptions.general')}
                      </SelectItem>
                      <SelectItem value="other" data-testid="service-option-other">
                        {t('form.serviceOptions.other')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Appointment DateTime Field */}
            <FormField
              control={form.control}
              name="appointment_datetime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('form.appointment')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      {...field}
                      className="border-gray-300 focus:border-[hsl(224,64%,33%)] focus:ring-2 focus:ring-[hsl(224,64%,33%)]/20"
                      data-testid="lead-form-appointment-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Details Field */}
            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {t('form.details')}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('form.detailsPlaceholder')}
                      {...field}
                      rows={4}
                      className="border-gray-300 focus:border-[hsl(224,64%,33%)] focus:ring-2 focus:ring-[hsl(224,64%,33%)]/20"
                      data-testid="lead-form-details-textarea"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[hsl(215,75%,16%)] text-white hover:bg-[hsl(224,64%,33%)] py-6 rounded-md font-semibold text-base"
              data-testid="lead-form-submit-button"
            >
              {isSubmitting ? t('form.submitting') : t('form.submit')}
            </Button>

            {/* Contact Info */}
            <div className="pt-4 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600 mb-2">Or call us directly:</p>
              <div className="flex flex-col space-y-1">
                <a
                  href="tel:+14074510902"
                  className="text-[hsl(224,64%,33%)] hover:text-[hsl(215,75%,16%)] font-semibold transition-colors duration-200"
                  data-testid="modal-phone-1"
                >
                  +1 (407) 451-0902
                </a>
                <a
                  href="tel:+14074961867"
                  className="text-[hsl(224,64%,33%)] hover:text-[hsl(215,75%,16%)] font-semibold transition-colors duration-200"
                  data-testid="modal-phone-2"
                >
                  +1 (407) 496-1867
                </a>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
